import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sparkles, RefreshCw, Check, X, AlertTriangle, 
  ChevronDown, ChevronUp, Zap, Clock, CheckCircle2,
  Loader2, SlidersHorizontal
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { API } from "../../lib/api";

const PRIORITY_COLORS = {
  HIGH: "bg-red-500/20 text-red-400 border-red-500/50",
  MEDIUM: "bg-amber-500/20 text-amber-400 border-amber-500/50",
  LOW: "bg-blue-500/20 text-blue-400 border-blue-500/50"
};

const PRIORITY_ICONS = {
  HIGH: "🔴",
  MEDIUM: "🟡",
  LOW: "🟢"
};

const STATUS_LABELS = {
  detected: "Detected",
  rewriting: "Rewriting...",
  fixed: "Fixed",
  skipped: "Skipped"
};

export const SmartRefineModal = ({ 
  open, 
  onOpenChange, 
  project, 
  projectId,
  onProjectUpdate 
}) => {
  const [chapterNumber, setChapterNumber] = useState(1);
  const [intensity, setIntensity] = useState("standard");
  const [maxFixes, setMaxFixes] = useState(15);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState(new Set());
  const [expandedIssue, setExpandedIssue] = useState(null);
  const pollRef = useRef(null);

  // Get available chapters
  const getChapterCount = () => {
    // First try chapter_outline (preferred)
    if (project?.chapter_outline?.length > 0) return project.chapter_outline.length;
    // Fallback to content regex
    if (!project?.content) return 0;
    const matches = project.content.match(/---\s*CHAPTER\s+\d+/gi);
    return matches ? matches.length : 0;
  };

  const chapterCount = getChapterCount();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Poll for job status
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/smart-refine/jobs/${jobId}`);
        setJob(res.data);

        if (res.data.status === "COMPLETED" || res.data.status === "FAILED") {
          if (pollRef.current) clearInterval(pollRef.current);
          setLoading(false);

          if (res.data.status === "COMPLETED") {
            const fixedCount = res.data.issues?.filter(i => i.status === "fixed").length || 0;
            toast.success(`Found ${res.data.issues?.length || 0} issues, fixed ${fixedCount}`);
            
            // Auto-select all fixed issues
            const fixedIds = res.data.issues
              ?.filter(i => i.status === "fixed")
              .map(i => i.id) || [];
            setSelectedIssues(new Set(fixedIds));
          } else {
            toast.error(res.data.error_message || "Refine failed");
          }
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    };

    pollRef.current = setInterval(poll, 2000);
    poll(); // Initial poll

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId]);

  const startRefine = async () => {
    if (chapterCount === 0) {
      toast.error("No chapters found in this project");
      return;
    }

    setLoading(true);
    setJob(null);
    setSelectedIssues(new Set());

    try {
      const res = await axios.post(`${API}/smart-refine/jobs`, {
        project_id: projectId,
        chapter_number: chapterNumber,
        intensity,
        max_fixes: maxFixes
      });

      setJobId(res.data.job_id);
      toast.info(`Analyzing Chapter ${chapterNumber}...`);
    } catch (err) {
      setLoading(false);
      toast.error(err.response?.data?.detail || "Failed to start refine");
    }
  };

  const applyFixes = async (applyAll = false) => {
    if (!jobId || !job) return;

    const issueIds = applyAll 
      ? job.issues.filter(i => i.status === "fixed").map(i => i.id)
      : Array.from(selectedIssues);

    console.log('Applying fixes:', { applyAll, issueIds, selectedIssues: Array.from(selectedIssues) });

    if (issueIds.length === 0) {
      toast.error("No fixes selected");
      return;
    }

    setApplying(true);

    try {
      // Send issue_ids in the request body as an object
      const res = await axios.post(`${API}/smart-refine/jobs/${jobId}/apply`, {
        issue_ids: issueIds
      });
      
      console.log('Apply response:', res.data);
      
      if (res.data.applied_count === 0) {
        toast.warning("No fixes could be matched in the text. The content may have changed.");
      } else {
        toast.success(`Applied ${res.data.applied_count} fixes!`);
      }
      
      onProjectUpdate?.({ word_count: res.data.new_word_count });
      
      // Refresh project content
      const projectRes = await axios.get(`${API}/project/${projectId}`);
      onProjectUpdate?.(projectRes.data);
      
      onOpenChange(false);
    } catch (err) {
      console.error('Apply error:', err.response?.data || err);
      toast.error(err.response?.data?.detail || "Failed to apply fixes");
    } finally {
      setApplying(false);
    }
  };

  const toggleIssue = (issueId) => {
    const newSet = new Set(selectedIssues);
    if (newSet.has(issueId)) {
      newSet.delete(issueId);
    } else {
      newSet.add(issueId);
    }
    setSelectedIssues(newSet);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "fixed": return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
      case "rewriting": return <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />;
      case "skipped": return <X className="w-4 h-4 text-slate-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const renderProgress = () => {
    if (!job) return null;

    const statusLabels = {
      PENDING: "Starting...",
      DETECTING: "Scanning for issues...",
      FLAGGING: "Prioritizing issues...",
      REWRITING: "Fixing issues...",
      COMPLETED: "Complete!",
      FAILED: "Failed"
    };

    return (
      <div className="space-y-3 p-4 bg-slate-800/50 rounded-lg border border-cyan-500/20">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-cyan-300">
            {statusLabels[job.status] || job.status}
          </span>
          <span className="text-sm text-slate-400">{job.progress}%</span>
        </div>
        <div className="w-full bg-slate-900 rounded-full h-2">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full transition-all duration-500"
            style={{ width: `${job.progress}%` }}
          />
        </div>
        {job.chunks_total > 0 && (
          <p className="text-xs text-slate-500">
            Processing chunk {job.chunks_processed} of {job.chunks_total}
          </p>
        )}
      </div>
    );
  };

  const renderIssueList = () => {
    if (!job?.issues?.length) return null;

    const fixedIssues = job.issues.filter(i => i.status === "fixed");
    const skippedIssues = job.issues.filter(i => i.status === "skipped");

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-200">
            Issues Found: {job.issues.length} 
            <span className="text-emerald-400 ml-2">({fixedIssues.length} fixed)</span>
            {skippedIssues.length > 0 && (
              <span className="text-slate-500 ml-2">({skippedIssues.length} skipped)</span>
            )}
          </h4>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-slate-600 hover:bg-slate-700 z-10"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const allFixed = fixedIssues.map(i => i.id);
                console.log('Select All clicked, selecting:', allFixed.length, 'issues');
                setSelectedIssues(new Set(allFixed));
              }}
              data-testid="smart-refine-select-all"
            >
              Select All
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs border-slate-600 hover:bg-slate-700 z-10"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedIssues(new Set());
              }}
              data-testid="smart-refine-clear"
            >
              Clear
            </Button>
          </div>
        </div>

        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-2">
            {job.issues.map((issue) => (
              <div 
                key={issue.id}
                className={`p-3 rounded-lg border transition-all cursor-pointer ${
                  issue.status === "fixed" 
                    ? selectedIssues.has(issue.id)
                      ? "bg-emerald-900/30 border-emerald-500/50"
                      : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                    : "bg-slate-800/30 border-slate-700/50 opacity-60"
                }`}
                onClick={() => issue.status === "fixed" && toggleIssue(issue.id)}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox for fixed issues */}
                  {issue.status === "fixed" && (
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                      selectedIssues.has(issue.id) 
                        ? "bg-emerald-500 border-emerald-500" 
                        : "border-slate-600"
                    }`}>
                      {selectedIssues.has(issue.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">{PRIORITY_ICONS[issue.priority]}</span>
                      <Badge className={`text-xs ${PRIORITY_COLORS[issue.priority]}`}>
                        {issue.issue_type?.replace(/_/g, " ")}
                      </Badge>
                      {getStatusIcon(issue.status)}
                      <span className="text-xs text-slate-500">{STATUS_LABELS[issue.status]}</span>
                    </div>
                    
                    <p className="text-xs text-slate-400 mb-2">{issue.explanation}</p>
                    
                    {/* Expandable before/after */}
                    <button
                      className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedIssue(expandedIssue === issue.id ? null : issue.id);
                      }}
                    >
                      {expandedIssue === issue.id ? (
                        <><ChevronUp className="w-3 h-3" /> Hide details</>
                      ) : (
                        <><ChevronDown className="w-3 h-3" /> Show before/after</>
                      )}
                    </button>
                    
                    {expandedIssue === issue.id && (
                      <div className="mt-3 space-y-2">
                        <div className="p-2 bg-red-900/20 rounded border border-red-500/30">
                          <p className="text-xs text-red-300 font-medium mb-1">Original:</p>
                          <p className="text-xs text-slate-300 line-through opacity-70">
                            {issue.original_text?.slice(0, 300)}
                            {issue.original_text?.length > 300 ? "..." : ""}
                          </p>
                        </div>
                        {issue.fixed_text && (
                          <div className="p-2 bg-emerald-900/20 rounded border border-emerald-500/30">
                            <p className="text-xs text-emerald-300 font-medium mb-1">Improved:</p>
                            <p className="text-xs text-slate-200">
                              {issue.fixed_text?.slice(0, 300)}
                              {issue.fixed_text?.length > 300 ? "..." : ""}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl bg-[#1F2937] border-cyan-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-300">
            <Sparkles className="w-5 h-5 text-purple-400" />
            Intelligent Rewrite (Nova)
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Scans your chapter for issues and fixes them automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Configuration */}
          {!loading && !job?.issues?.length && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Chapter Selection */}
              <div className="space-y-2">
                <Label className="text-slate-200 font-medium">Chapter</Label>
                <Select 
                  value={String(chapterNumber)} 
                  onValueChange={(v) => setChapterNumber(parseInt(v))}
                >
                  <SelectTrigger className="bg-slate-800 border-cyan-500/25 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-cyan-500/20">
                    {Array.from({ length: chapterCount }, (_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        Chapter {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Intensity */}
              <div className="space-y-2">
                <Label className="text-slate-200 font-medium">Intensity</Label>
                <Select value={intensity} onValueChange={setIntensity}>
                  <SelectTrigger className="bg-slate-800 border-cyan-500/25 text-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-cyan-500/20">
                    <SelectItem value="light">Light (HIGH only)</SelectItem>
                    <SelectItem value="standard">Standard (HIGH + MED)</SelectItem>
                    <SelectItem value="pro">Pro (All issues)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Max Fixes */}
              <div className="space-y-2">
                <Label className="text-slate-200 font-medium">Max Fixes: {maxFixes}</Label>
                <Slider
                  value={[maxFixes]}
                  onValueChange={([v]) => setMaxFixes(v)}
                  min={5}
                  max={30}
                  step={5}
                  className="mt-3"
                />
              </div>
            </div>
          )}

          {/* Progress */}
          {loading && renderProgress()}

          {/* Issue List */}
          {job?.status === "COMPLETED" && renderIssueList()}

          {/* Error */}
          {job?.status === "FAILED" && (
            <div className="p-4 bg-red-900/30 rounded-lg border border-red-500/50">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" />
                <span>{job.error_message || "Something went wrong"}</span>
              </div>
            </div>
          )}

          {/* No issues found */}
          {job?.status === "COMPLETED" && job.issues?.length === 0 && (
            <div className="p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-emerald-300">Looking Good!</h3>
              <p className="text-slate-400 text-sm mt-1">
                No significant issues found in Chapter {chapterNumber}. Your writing is solid!
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="border-slate-600 text-slate-300 w-full sm:w-auto"
          >
            Close
          </Button>

          {!loading && !job?.issues?.length && (
            <Button
              onClick={startRefine}
              disabled={chapterCount === 0}
              className="bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-700 hover:to-purple-700 w-full sm:w-auto"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Scan Chapter {chapterNumber}
            </Button>
          )}

          {job?.status === "COMPLETED" && job.issues?.some(i => i.status === "fixed") && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => applyFixes(false)}
                disabled={applying || selectedIssues.size === 0}
                className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-900/30 w-full sm:w-auto"
              >
                {applying ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <>Save Selected ({selectedIssues.size})</>
                )}
              </Button>
              <Button
                onClick={() => applyFixes(true)}
                disabled={applying}
                className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
              >
                <Check className="w-4 h-4 mr-2" />
                Save All to Story
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SmartRefineModal;
